import { useState, useEffect } from "react";

import { useAuthStore } from "../../../stores/authStore";
import { userService } from "../../../lib/api/users";
import { useAlertStore } from "../../../stores/alertStore";
import { useTranslation } from "react-i18next";
import { Logo } from "../../../components/common/Logo";

export const SetCurrencyTimezoneModal = () => {
    const { t } = useTranslation("common");
    const { user, setUser, isAuthenticated } = useAuthStore();
    const { showAlert } = useAlertStore();

    // Show only if authenticated, (email verified OR skipped), but missing currency or timezone
    const skipVerification = window.__DASHBAL_CONFIG__?.skipEmailVerification;
    const isOpen = Boolean(
        isAuthenticated &&
        (user?.emailVerified || skipVerification) &&
        (!user?.currency || !user?.timezone)
    );

    const [currency, setCurrency] = useState("");
    const [timezone, setTimezone] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [availableCurrencies, setAvailableCurrencies] = useState<any[]>([]);

    useEffect(() => {
        const fetchCurrencies = async () => {
            try {
                // Import dynamically to avoid circular dependencies if any
                const { currencyService } = await import("../../../lib/api/currency");
                const response = await currencyService.getCurrencies();
                if (response.success) {
                    setAvailableCurrencies(response.data.currencies);
                }
            } catch (error) {
                console.error("Failed to fetch currencies", error);
                // Fallback or just empty list (user will see empty dropdown)
            }
        };
        fetchCurrencies();
    }, []);

    // Initialize state from user if available (e.g. one might be set but not the other)
    // AND automatically detect location if missing
    useEffect(() => {
        if (!isOpen) return;

        const initializeSettings = async () => {
            let initialCurrency = "";
            let initialTimezone = "";

            if (user) {
                if (user.currency) initialCurrency = user.currency;
                if (user.timezone) initialTimezone = user.timezone;
            }

            // If still missing any of them, try to detect from IP
            if (!initialCurrency || !initialTimezone) {
                try {
                    const response = await userService.detectLocation();
                    if (response.success && response.data) {
                        if (!initialCurrency && response.data.currency) {
                            initialCurrency = response.data.currency;
                        }
                        if (!initialTimezone && response.data.timezone) {
                            initialTimezone = response.data.timezone;
                        }
                    }
                } catch (error) {
                    console.warn("Failed to detect location", error);
                }
            }

            setCurrency(initialCurrency);
            setTimezone(initialTimezone);
        };

        initializeSettings();
    }, [user, isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!currency || !timezone) {
            showAlert({ message: "Please select both currency and timezone", type: "error" });
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await userService.updateProfile({
                currency,
                timezone
            });

            if (response.success && response.data.user) {
                // Update local store
                setUser({
                    ...user!,
                    currency: response.data.user.currency,
                    timezone: response.data.user.timezone
                });


            }
        } catch (error: any) {
            console.error("Failed to update profile", error);
            showAlert({
                message: error.response?.data?.error || "Failed to save settings",
                type: "error"
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4 text-center">
                {/* Overlay - blocked interaction */}
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity modal-backdrop" />

                {/* Modal */}
                <div className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-neutral-900 p-6 text-left align-middle shadow-xl transition-all">
                    <div className="flex justify-center mb-6 h-8">
                        <h3 className="relative text-lg font-bold leading-6 text-gray-900 dark:text-neutral-100">
                            <div className="absolute right-full mr-3 flex items-center gap-2 whitespace-nowrap h-full">
                                <Logo className="w-6 h-6" />
                                <span className="w-4 h-4 text-neutral-300 dark:text-neutral-600" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_cc5012f2.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_cc5012f2.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                            </div>
                            {t("auth.welcomeToDashbal", "Welcome to Dashbal")}
                        </h3>
                    </div>
                    <div className="mt-2 text-center text-sm text-gray-500 dark:text-neutral-400 mb-6">
                        <p>To get started, please set your preferred currency and timezone.</p>
                        <p className="text-xs mt-1 text-amber-600 dark:text-amber-500 bg-amber-50 dark:bg-amber-900/20 py-1 px-2 rounded inline-block">
                            These settings are required to continue.
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                Currency
                            </label>
                            <select
                                value={currency}
                                onChange={(e) => setCurrency(e.target.value)}
                                className="input w-full"
                                required
                            >
                                <option value="" disabled>Select Currency</option>
                                {availableCurrencies.map((c) => (
                                    <option key={c.code} value={c.code}>
                                        {c.code} - {c.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                Timezone
                            </label>
                            <select
                                value={timezone}
                                onChange={(e) => setTimezone(e.target.value)}
                                className="input w-full"
                                required
                            >
                                <option value="" disabled>Select Timezone</option>
                                {[
                                    "UTC",
                                    "America/New_York",
                                    "America/Chicago",
                                    "America/Denver",
                                    "America/Los_Angeles",
                                    "Europe/London",
                                    "Europe/Paris",
                                    "Europe/Berlin",
                                    "Asia/Dubai",
                                    "Asia/Kolkata",
                                    "Asia/Singapore",
                                    "Asia/Tokyo",
                                    "Australia/Sydney"
                                ].map(tz => (
                                    <option key={tz} value={tz}>{tz}</option>
                                ))}
                                {timezone && ![
                                    "UTC", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
                                    "Europe/London", "Europe/Paris", "Europe/Berlin", "Asia/Dubai", "Asia/Kolkata",
                                    "Asia/Singapore", "Asia/Tokyo", "Australia/Sydney"
                                ].includes(timezone) && (
                                        <option key={timezone} value={timezone}>{timezone}</option>
                                    )}
                            </select>
                        </div>

                        <div className="mt-8">
                            <button
                                type="submit"
                                disabled={isSubmitting || !currency || !timezone}
                                className="btn-primary w-full py-3"
                            >
                                {isSubmitting ? (
                                    <span className="flex items-center justify-center">
                                        <div className="spinner mr-2 border-white" />
                                        Saving...
                                    </span>
                                ) : (
                                    "Save & Continue"
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
